import {useShop} from "@/providers/ShopProvider";
import { CartStep, useCart } from '@/providers/CartProvider';
import PaymentStatus from './PaymentStatus';
import {Panel} from '@/components/shared/Panel';
import { shopVar } from '@/lib/shopStyles';
import {MainButton} from '@/components/shared/MainButton';
import { RiArrowLeftSLine, RiArrowRightSLine } from '@remixicon/react';
import { useEvent } from '@/providers/EventProvider';
import { OrganisationLegalInformation } from '@/components/OrganisationLegalInformation';
import { useLanguage } from '@/providers/LanguageProvider';
import * as m from "@/paraglide/messages.js"
import { SelectPaymentMethod } from './SelectPaymentMethod';
import { handleRedirect } from '@/lib/iframeRedirect';
import { getProduct, GroupedProduct, GroupedProductItem } from "@/lib/TicketApi";
import { Money } from "@/lib/money";
import React from "react";
import ProductSection from '@/components/purchase/product-pages/ProductSection';
import { Fees } from '@/components/shared/Fees';

export function Checkout() {
    const {shop} = useShop(); 
    const {cart, costOverview, paymentMethod, setPaymentMethod, startCheckout, loading, setStep, error} = useCart();
    const {eventId, event} = useEvent();

    const { currentLanguage: lang } = useLanguage();
    const { organisationLegalInformation } = shop;

    const startPayment = async () => {
        try {
            const paymentResponse = await startCheckout(eventId);
            if(paymentResponse.type === "redirect") {
                handleRedirect(paymentResponse.url as string);
            }
        } catch (error) {
            // Error is already handled in CartProvider and displayed via the error state
            console.error('Checkout failed:', error);
        }
    }

    if(cart === null){
        return <div>Loading...</div>;
    }

    const overview = costOverview();

    const rootUrl = import.meta.env.VITE_SHOP_ROOT_URL;

    return (
        <>
        {!overview.total.isZero() && cart.pendingPayment === null && cart.isCheckedOut === false && (
            <Panel>
                <SelectPaymentMethod 
                    paymentMethods={shop.paymentMethods}
                    selectedMethod={paymentMethod}
                    onMethodChange={setPaymentMethod}
                    totalAmount={overview.totalExPaymentFee}
                />
            </Panel>
        )}
        

        {!cart.isCheckedOut && cart.pendingPayment === null && (<>
            {(() => {
                const upsellPage = event.productPages.find(p => p.slug === 'checkout_upsell');
                if (!upsellPage) return null;
                const hasProducts = upsellPage.productSections.some(section => section.products.length > 0);
                if (!hasProducts) return null;
                return (
                    <div className="">
                        {upsellPage.productSections.map(section =>
                            section.products.length > 0 ? (
                                <ProductSection key={section.id} productSection={section} />
                            ) : null
                        )}
                    </div>
                );
            })()}
        </>
        )}
        {/* --- End Upsell Section --- */}
        <Panel>
            <PaymentStatus />
            {overview.products.map((product) => {
                    const fullProduct = getProduct(shop, product.productId);
                    const isGrouped = fullProduct?.type === 'grouped_product';
                    const groupedProductData = fullProduct as GroupedProduct;

                    return (
                        <React.Fragment key={product.productId}>
                            <div className="flex items-center justify-between py-2">
                                <div className='flex flex-col'>
                                    <span className='font-semibold py-1'>{product.quantity}x {product.name}</span>
                                    {/* Show unit price + fee for BOTH grouped and non-grouped */} 
                                    <span className='text-sm tracking-tight'>
                                        {product.unit.display(lang)} <Fees serviceFees={getProduct(shop, product.productId)?.serviceFees} locale={lang} />
                                    </span>
                                </div>
                                <div className='flex flex-col items-end'>
                                    <span className='font-bold text-xl'>{product.totalInclFee.display(lang)}</span>
                                </div>
                            </div>
                            {/* Updated breakdown list */} 
                            {isGrouped && groupedProductData.products && (
                                <ul className="list-none pl-6 -mt-2 mb-2 text-xs border-l border-dashed ml-2" style={{ borderColor: shopVar('--shop-borderColor') }}>
                                    {groupedProductData.products.map((subProduct: GroupedProductItem, index: number) => {
                                        const subProductUnitPrice = Money.fromInteger(subProduct.price.amount, subProduct.price.currency);
                                        
                                        return (
                                            <li key={`${subProduct.productId}-${index}`} className="flex justify-between items-center py-0.5">
                                                <span style={{ color: shopVar('--shop-textColor') }}>
                                                    {subProduct.quantity}x {subProduct.name}
                                                </span>
                                                {/* Display unit price + unit fee for sub-product */}
                                                <span style={{ color: shopVar('--shop-textColor') }}>
                                                    {subProductUnitPrice.display(lang)}
                                                    <Fees serviceFees={subProduct.serviceFees} locale={lang} />
                                                </span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </React.Fragment>
                    );
                })}
                <div className='flex flex-col items-end gap-2 mt-2 pt-2 border-t border-dashed' style={{
                    borderColor: shopVar('--shop-borderColor')
                }}>
                    {overview.paymentFee && (
                        <>
                            <div className='text-sm tracking-tight'>{m.subtotal()}: {overview.total.subtract(overview.paymentFee.total).display(lang)}</div>
                            <div className='text-sm tracking-tight'>{overview.paymentFee.name}: {overview.paymentFee.total.display(lang)}</div>
                        </>
                    )}
                    <div className='font-bold text-2xl'>{m.total()} {overview.total.display(lang)}</div>
                </div>
                {!cart.isCheckedOut && cart.pendingPayment === null && (<>
                    <div className='mt-4 mb-2 flex gap-2'>
                        <div className='border-2 border-black rounded-md p-2 hover:cursor-pointer hover:opacity-80 hover:bg-gray-100' onClick={() => {
                            setStep(CartStep.Order);
                            }}>
                            <RiArrowLeftSLine className="h-10 w-10 shrink-0 group-hover:translate-x-2 group-hover:scale-110 transition-transform duration-200"/>
                        </div>
                        <MainButton onClick={startPayment} disabled={loading} loading={loading} className='flex-grow'>
                            <div className='text-lg font-bold leading-7'>{loading ? `${m.loading_button_prefix()} ${overview.total.isZero() ? m.loading_btn_tickets_vartiant() : m.loading_button_payment_varitant()}` : Object.values(cart.payments).length !== 0 ? m.retry_payment() : overview.total.isZero() ? m.checkout_claim_tickets() : m.checkout_proceed_to_payment()}</div>
                            <RiArrowRightSLine className="h-10 w-10 shrink-0 group-hover:translate-x-2 group-hover:scale-110 transition-transform duration-200"/>
                        </MainButton>
                </div>
                
                {error && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md" style={{
                        backgroundColor: shopVar('--shop-productErrorMessageBackground') || '#fef2f2',
                        color: shopVar('--shop-productErrorMessageText') || '#dc2626',
                        borderColor: shopVar('--shop-productErrorMessageText') || '#fecaca'
                    }}>
                        <div className="text-sm font-medium">{error}</div>
                    </div>
                )}
                
                {organisationLegalInformation.showInFooter === false && (
                    <OrganisationLegalInformation/>
                )}
                </>)}
                {cart.pendingPayment !== null && (<>
                    <MainButton disabled={true} className='mt-4 mb-2'>
                        {m.processing_payment()}
                    </MainButton>
                </>)}
                {cart.isCheckedOut && (<>
                    <MainButton disabled={false} url={`${rootUrl}/order/${cart.orderId}`} newTab={!window.location.href.includes(rootUrl)} className='mt-4 mb-2'>
                        <div className='text-lg font-bold leading-7'>{m.cart_is_checked_out_collect_order()}</div>
                        <RiArrowRightSLine className="h-10 w-10 shrink-0 group-hover:translate-x-2 group-hover:scale-110 transition-transform duration-200"/>
                    </MainButton>
                </>)}
        </Panel>
        </>
    );
}